// =============================================================================
// app/src/paginas/Fluxo.tsx — a linha do tempo do dinheiro (/fluxo, só dono).
//
// Pedido do dono (23/08/2026): "um design com fluxo de caixa em gráfico numa
// tela separada" — passado realizado + futuro previsto (decisão dele).
//
// A pergunta que a tela responde: "vou ter dinheiro para pagar o dia 10?".
// O que entra no futuro: contas a pagar (as fixas já materializadas), folha
// em aberto, saldo de encomendas com valor combinado. O que fica FORA e a
// tela mostra à parte: o fiado (não tem vencimento — inventar data seria
// mentir) e as contas "a definir" (contadas, nunca somadas).
//
// -----------------------------------------------------------------------------
// O TERCEIRO MODO, "12 MESES (SIMULADO)" — etapa C do simulador (27/09/2026)
//
// Pedido do dono: "o fluxo de caixa simulado com base no que estamos tendo de
// receita e também com as minhas métricas". As duas janelas curtas continuam
// como eram: puro banco, nada projetado. O modo de 12 meses junta duas fontes,
// e a regra que impede contar a mesma saída duas vezes é por FONTE, não por
// data — está escrita em `projetarCaixa`, em app/src/lib/simulacao.ts.
//
// POR QUE ELE PRECISA EXISTIR, medido em produção em 27/09/2026: a RPC só
// conhece o que está LANÇADO, e o lançado acaba. Boleto de fornecedor existe
// até nov/2026; de dez/26 em diante o futuro inteiro do banco são R$ 2.811/mês
// de retirada. A folha existe numa competência só (set/2026). Um gráfico de 12
// meses feito só com a RPC mostraria uma loja sem custo nenhum a partir de
// dezembro — e o zero do banco é indistinguível de "não foi lançado".
//
// O MOTOR VEM POR IMPORT DINÂMICO: são 1.000 linhas de projeção que o balcão
// nunca abre. Esta tela é import estático no App.tsx, então trazer o motor de
// forma estática o colocaria no bundle principal, que a loja baixa no 4G.
// =============================================================================
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FluxoDia } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import CartaoKPI from '../componentes/CartaoKPI';
import GraficoFluxo from '../componentes/GraficoFluxo';
import Segmentado from '../componentes/Segmentado';
import {
  baseDaSimulacao, cenariosFinanceiros, fluxoDeCaixa, listarFiados, mesesDoCenario,
} from '../lib/consultas';
import {
  diasDeFuturoParaMeses, escalaDosPontos, pontosDoGrafico, resumoDaJanela,
} from '../lib/fluxo';
import { brl } from '../lib/formato';
import { dataCurtaDeISO } from '../lib/orcamento';
import { mensagemDeErro } from '../lib/supabase';
import { useFolhaPrevista } from '../lib/useFolhaPrevista';

const MESES_SIMULADOS = 12;

const JANELAS = [
  { id: 'quinze', rotulo: 'Próximos 15 dias', passado: 30, futuro: 15, simulado: false },
  { id: 'noventa', rotulo: 'Próximos 90 dias', passado: 30, futuro: 90, simulado: false },
  { id: 'ano', rotulo: '12 meses (simulado)', passado: 30, futuro: null, simulado: true },
] as const;

/** O dia de hoje do aparelho, só para dimensionar a janela da RPC. O relógio
 *  que vale para a projeção é o `hoje` que o banco devolve na base. */
function hojeLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function Fluxo() {
  const [janela, setJanela] = useState<(typeof JANELAS)[number]['id']>('quinze');
  const cfg = JANELAS.find((j) => j.id === janela)!;
  const [cenarioId, setCenarioId] = useState<number | null>(null);

  const futuro = cfg.futuro ?? diasDeFuturoParaMeses(hojeLocal(), MESES_SIMULADOS);

  // 094: a folha do mês corrente e do seguinte nasce do combinado ao abrir a
  // tela — sem isto, o salário que ninguém lançou no Painel não aparecia no
  // futuro. O fluxo espera a geração, mas não depende de ela dar certo.
  const prevista = useFolhaPrevista();
  const fluxo = useQuery({
    queryKey: ['fluxo', cfg.passado, futuro],
    queryFn: () => fluxoDeCaixa(cfg.passado, futuro),
    enabled: prevista.pronto,
  });
  // O fiado fica FORA da linha do tempo (sem vencimento confiável) — entra
  // como total à parte, da RPC que a tela de Fiado já usa.
  const fiados = useQuery({ queryKey: ['fiados'], queryFn: () => listarFiados(false) });

  // A base é CARA (a DRE dentro dela varre a história): uma chamada, guardada
  // por meia hora. Só é buscada quando o modo simulado está aberto.
  const base = useQuery({
    queryKey: ['base-simulacao', 6],
    queryFn: () => baseDaSimulacao(6),
    enabled: cfg.simulado && prevista.pronto,
    staleTime: 30 * 60 * 1000,
  });
  const cenarios = useQuery({
    queryKey: ['cenarios'],
    queryFn: cenariosFinanceiros,
    enabled: cfg.simulado,
    staleTime: 5 * 60 * 1000,
  });
  const cenario = (cenarios.data ?? []).find((c) => c.id === cenarioId)
    ?? (cenarios.data ?? []).find((c) => c.padrao)
    ?? (cenarios.data ?? [])[0]
    ?? null;
  // Os ajustes de mês são a SEGUNDA tabela da 085: esquecê-los não dá erro,
  // só ignora em silêncio o feriado que o dono marcou em novembro.
  const ajustes = useQuery({
    queryKey: ['cenario-meses', cenario?.id],
    queryFn: () => mesesDoCenario(cenario!.id),
    enabled: cfg.simulado && cenario != null,
    staleTime: 5 * 60 * 1000,
  });

  const simulacao = useQuery({
    queryKey: ['fluxo-simulado', cfg.passado, futuro, cenario?.id, ajustes.data?.length ?? 0],
    enabled: cfg.simulado && !!fluxo.data && !!base.data && !!cenario && ajustes.isSuccess,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      // Dinâmico de propósito: o motor não entra no bundle do balcão.
      const { fluxoSimulado } = await import('../lib/simulacao');
      return fluxoSimulado(fluxo.data!, base.data!, cenario!, MESES_SIMULADOS, ajustes.data ?? []);
    },
  });

  // No modo simulado a série desenhada é a EMENDADA; nos outros, a do banco.
  const dias: FluxoDia[] = cfg.simulado ? (simulacao.data?.dias ?? []) : (fluxo.data ?? []);
  const pontos = useMemo(() => pontosDoGrafico(dias), [dias]);
  const resumo = useMemo(() => resumoDaJanela(dias), [dias]);
  const fiadoAberto = (fiados.data ?? []).reduce((s, f) => s + Number(f.saldo), 0);
  const escala = escalaDosPontos(dias.length);

  const cobertura = simulacao.data?.cobertura ?? [];
  const avisos = useMemo(() => {
    const vistos = new Set<string>();
    for (const c of cobertura) for (const a of c.avisos) vistos.add(a);
    return [...vistos];
  }, [cobertura]);
  const faltando = simulacao.data?.premissas.faltando ?? [];
  const carregandoSimulado = cfg.simulado
    && (fluxo.isPending || base.isPending || cenarios.isPending || simulacao.isPending);
  const erro = fluxo.error ?? base.error ?? cenarios.error ?? ajustes.error ?? simulacao.error;

  return (
    <Layout titulo="Fluxo de caixa">
      <div className="space-y-4">
        <Segmentado itens={JANELAS.map((j) => ({ id: j.id, rotulo: j.rotulo }))}
                    valor={janela} aoMudar={setJanela} />

        {cfg.simulado && (
          <>
            {/* A faixa não se esconde: o molde é a faixa de ambiente do Layout,
                que existe exatamente para não se confundir o que é real com o
                que não é. */}
            <div className="rounded border border-dashed border-edge bg-panel-alt px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-dim">
                Simulação — não é o fechamento
              </p>
              <p className="mt-1 text-xs text-dim">
                Até <strong>hoje</strong> é o que aconteceu. Daí para a frente, o
                que está <strong>lançado</strong> no banco (contas, folha,
                encomendas) mais o que o cenário <strong>projeta</strong> — as
                barras apagadas. Nada foi somado duas vezes: onde o banco tem, o
                banco vence.
              </p>
            </div>

            {(cenarios.data ?? []).length > 1 && (
              <Segmentado
                itens={(cenarios.data ?? []).map((c) => ({ id: String(c.id), rotulo: c.nome }))}
                valor={String(cenario?.id ?? '')}
                aoMudar={(id) => setCenarioId(Number(id))}
              />
            )}
            {cenario?.descricao && <p className="text-xs text-dim">{cenario.descricao}</p>}
          </>
        )}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <CartaoKPI rotulo="Entra previsto" valor={brl(resumo.entraPrevisto)} tom="ok" />
          <CartaoKPI rotulo="Sai previsto" valor={brl(resumo.saiPrevisto)} tom="erro" />
          <CartaoKPI rotulo="Pior dia à frente"
                     valor={resumo.piorDia ? brl(resumo.piorDia.acumulado) : '—'}
                     detalhe={resumo.piorDia ? dataCurtaDeISO(resumo.piorDia.dia) : undefined} />
          <CartaoKPI rotulo="Fiado a receber" valor={brl(fiadoAberto)}
                     detalhe="sem data — fora do gráfico" />
        </div>

        {resumo.semValor > 0 && (
          <Aviso tipo="alerta">
            {resumo.semValor} conta{resumo.semValor > 1 ? 's' : ''} de valor
            variável (luz, água…) na janela, <strong>fora da soma</strong> — o
            "sai previsto" real é maior que o número acima.
          </Aviso>
        )}

        {cfg.simulado && faltando.length > 0 && (
          <Aviso tipo="alerta">
            <strong>O que falta para esta conta ficar inteira:</strong>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {faltando.map((f) => <li key={f}>{f}</li>)}
            </ul>
          </Aviso>
        )}

        {cfg.simulado && avisos.length > 0 && (
          <Aviso tipo="info">
            <strong>De onde vem o que está desenhado:</strong>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {avisos.map((a) => <li key={a}>{a}</li>)}
            </ul>
          </Aviso>
        )}

        {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}
        {(cfg.simulado ? carregandoSimulado : fluxo.isPending) && (
          <Carregando texto={cfg.simulado ? 'Medindo a loja e projetando 12 meses' : 'Montando a linha do tempo'} />
        )}

        {pontos.length > 0 && (
          <section className="cartao p-4">
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="rotulo">O dinheiro no tempo</h2>
              <span className="text-[0.625rem] uppercase tracking-wide text-dim">
                {escala === 'semana' ? 'uma barra por semana · ' : ''}toque numa barra para o detalhe
              </span>
            </div>
            <GraficoFluxo pontos={pontos} escala={escala} />
            <p className="mt-2 text-xs text-dim">
              A linha branca é a <strong>variação acumulada na janela</strong> —
              quanto o caixa anda, não quanto tem: o sistema não conhece o saldo
              bancário da loja.
            </p>
          </section>
        )}

        {cfg.simulado && cobertura.length > 0 && (
          <section className="cartao p-4">
            <h2 className="rotulo mb-2">Mês a mês, o que é lançado e o que é projeção</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-dim">
                    <th className="pb-1">Mês</th>
                    <th className="pb-1 text-right">Venda projetada</th>
                    <th className="pb-1 text-right">Fornecedor lançado</th>
                    <th className="pb-1 text-right">Compra projetada</th>
                    <th className="pb-1">Folha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {cobertura.map((c, i) => (
                    <tr key={c.mes}>
                      <td className="py-1">{c.mes}</td>
                      <td className="py-1 text-right">
                        {brl(simulacao.data?.meses[i]?.venda ?? 0)}
                      </td>
                      <td className="py-1 text-right">
                        {c.fornecedorLancado > 0 ? brl(c.fornecedorLancado) : '—'}
                      </td>
                      <td className="py-1 text-right">
                        {c.mercadoriaProjetada == null ? 'sem custo' : brl(c.mercadoriaProjetada)}
                      </td>
                      <td className="py-1 text-dim">
                        {c.folhaProjetada ? 'projetada'
                          : c.encargoProjetado ? 'lançada (encargo projetado)' : 'lançada'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-dim">
              A <strong>compra projetada</strong> é o que falta para a mercadoria
              do mês chegar ao custo do cenário, depois de descontar o boleto que
              já está no banco. Onde o boleto cobre, ela é zero — é assim que a
              mesma compra não entra duas vezes.
            </p>
          </section>
        )}
      </div>
    </Layout>
  );
}
